import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../state/app.state';
import {
  AddDocumentRequest,
  DeleteDocumentRequest,
  DeleteFolderRequest,
  UpdateProjectDocumentsBreadcrumbs,
} from '../../../../../../core/projects.actions';
import { projectDocumentsBreadcrumbs, projectDocumentsCurrentRootId } from '../../../../../../core/projects.selectors';
import { ActivatedRoute } from '@angular/router';
import { EditNameComponent } from '../edit-name/edit-name.component';
import { Subject } from 'rxjs';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { AppService } from '../../../../../../../../shared/services';
import { takeUntil } from 'rxjs/operators';
import { AnalyticsService } from '../../../../../../../../shared/services/analytics.service';

@Component({
    selector: 'app-documents-table',
    templateUrl: './documents-table.component.html',
    styleUrls: ['./documents-table.component.scss'],
    standalone: false
})
export class DocumentsTableComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('fileInputForDocumentUpload', { static: false }) fileInputRef: any;
  @Input() data: any;
  @Input() canEdit = false;

  dataSource: any = [];
  displayedColumns: string[] = ['name', 'size', 'filesVersions', 'lastModified', 'menu'];
  expandedRows = new Set<string>();
  thumbnailErrors = new Set<string>();
  projectId;
  breadcrumbs: Array<{ id: string; name: string }> = [];
  currentRootId: string | null = null;

  /**
   * Deep link from global search: `?documentId=` navigates the in-memory
   * breadcrumb to the document's folder and opens the document the way a row
   * click does. One-shot, consumed on the first data load that contains it.
   */
  private pendingDeepLinkDocumentId: string = null;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public dialog: MatDialog,
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    public commonService: CommonService,
    private appService: AppService,
    private analyticsService: AnalyticsService,
  ) {}

  ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.pendingDeepLinkDocumentId = this.route.snapshot.queryParamMap.get('documentId');

    this.commonService.triggerAddDocument.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.fileInputRef.nativeElement.click();
    });

    this.store
      .select(projectDocumentsBreadcrumbs)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(breadcrumbs => {
        this.breadcrumbs = breadcrumbs || [];
      });

    this.store
      .select(projectDocumentsCurrentRootId)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(currentRootId => {
        this.currentRootId = currentRootId;
        this.processData();
      });

    this.processData();
    this.tryOpenDeepLinkedDocument();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && !changes['data'].firstChange) {
      if (this.currentRootId) {
        this.expandedRows.add(this.currentRootId);
      }
      this.breadcrumbs.forEach(breadcrumb => {
        this.expandedRows.add(breadcrumb.id);
      });

      this.processData();
      this.tryOpenDeepLinkedDocument();
    }
  }

  /**
   * Auto-opens a deep-linked document (?documentId=) once the documents tree
   * has loaded: navigates the in-memory breadcrumb to the document's folder
   * and opens the same carousel a row click would. One-shot; an unknown id
   * (deleted document) no-ops and leaves the user at the folder root.
   */
  private tryOpenDeepLinkedDocument(): void {
    if (!this.pendingDeepLinkDocumentId) {
      return;
    }

    const rootData = this.data?.data || this.data;
    const flattenedRows = rootData?.flattenedRows || [];
    if (!flattenedRows.length) {
      // Tree not loaded yet — keep the pending id for the next data change.
      return;
    }

    const id = this.pendingDeepLinkDocumentId;
    const target = flattenedRows.find(
      row => row.type === 'document' && `${row.id}` === `${id}`,
    );
    if (!target) {
      // Keep the pending id: this tree may be the previous project's (stale
      // store replay) and the document can appear on the next data change. A
      // genuinely deleted/unknown id simply never matches — a permanent no-op.
      return;
    }
    this.pendingDeepLinkDocumentId = null;

    const parentFolder = target.parentId
      ? flattenedRows.find(row => row.type === 'folder' && row.id === target.parentId)
      : null;
    if (parentFolder) {
      this.navigateToFolder(parentFolder);
    }

    // Open outside the current change-detection pass (MatDialog), then scroll
    // the document's row (now shown by the breadcrumb navigation) into view.
    // The table has no persistent selected-row styling on manual clicks, so
    // none is applied here either.
    setTimeout(() => {
      this.openDocumentCarousel(new Event('click'), target);
      const index = (this.dataSource || [])
        .findIndex(row => row.type === 'document' && row.id === target.id);
      if (index >= 0) {
        document.querySelectorAll('app-documents-table tr.table-row')[index]
          ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  }

  processData() {
    this.thumbnailErrors.clear();

    if (this.data) {
      const rootData = this.data.data || this.data;
      const flattenedRows = rootData.flattenedRows || [];

      if (!flattenedRows.length) {
        this.dataSource = [];
        return;
      }

      const rowMap = new Map<string, any>(flattenedRows.map((row: any) => [row.id, row]));

      if (this.currentRootId) {
        const currentRoot = rowMap.get(this.currentRootId);
        if (!currentRoot) {
          this.currentRootId = null;
          this.breadcrumbs = [];
        } else {
          const rootLevel = currentRoot.level;

          this.dataSource = flattenedRows
            .filter((row: any) => {
              if (row.parentId === this.currentRootId) {
                return true;
              }

              if (row.parentId && row.level > rootLevel + 1) {
                let currentParentId: string | undefined = row.parentId;
                while (currentParentId) {
                  const parentRow = rowMap.get(currentParentId);
                  if (!parentRow) {
                    break;
                  }

                  if (parentRow.id === this.currentRootId) {
                    return true;
                  }

                  if (!this.expandedRows.has(currentParentId)) {
                    return false;
                  }

                  currentParentId = parentRow.parentId;
                }
              }

              return false;
            })
            .map((row: any) => {
              if (row.parentId === this.currentRootId) {
                return { ...row, level: 0 };
              }
              const adjustedLevel = row.level - (rootLevel + 1);
              return { ...row, level: Math.max(0, adjustedLevel) };
            });

          return;
        }
      }

      this.dataSource = flattenedRows.filter((row: any) => {
        if (!row.parentId) {
          return true;
        }

        let currentParentId: string | undefined = row.parentId;
        while (currentParentId) {
          if (!this.expandedRows.has(currentParentId)) {
            return false;
          }
          const parentRow = rowMap.get(currentParentId);
          currentParentId = parentRow?.parentId;
        }

        return true;
      });
    }
  }

  toggleRow(row: any) {
    if (!row.hasChildren) {
      return;
    }

    if (this.expandedRows.has(row.id)) {
      this.expandedRows.delete(row.id);
    } else {
      this.expandedRows.add(row.id);
    }

    this.processData();
  }

  navigateToFolder(row: any) {
    if (row.type !== 'folder') {
      return;
    }

    const rootData = this.data.data || this.data;
    const flattenedRows = rootData.flattenedRows || [];

    if (!flattenedRows.length) {
      return;
    }

    const rowMap = new Map<string, any>(flattenedRows.map((r: any) => [r.id, r]));

    const clickedFolderId = row.id;

    let targetRow = rowMap.get(clickedFolderId);

    if (!targetRow && row.originalData) {
      targetRow = rowMap.get(row.originalData.id);
    }

    if (!targetRow) {
      return;
    }

    const path: Array<{ id: string; name: string }> = [];
    let currentRow: any = targetRow;
    const visited = new Set<string>();

    while (currentRow && !visited.has(currentRow.id)) {
      visited.add(currentRow.id);

      path.unshift({
        id: currentRow.id,
        name: currentRow.name,
      });

      if (!currentRow.parentId) {
        break;
      }

      const parentRow = rowMap.get(currentRow.parentId);
      if (!parentRow) {
        break;
      }

      currentRow = parentRow;
    }

    const breadcrumbs = path;

    this.store.dispatch(
      UpdateProjectDocumentsBreadcrumbs({
        breadcrumbs,
        currentRootId: clickedFolderId,
      })
    );

    this.expandedRows.clear();
    this.processData();
  }

  navigateToBreadcrumb(index: number) {
    if (index === -1) {
      this.store.dispatch(
        UpdateProjectDocumentsBreadcrumbs({
          breadcrumbs: [],
          currentRootId: null,
        })
      );
    } else {
      const targetBreadcrumb = this.breadcrumbs[index];
      const updatedBreadcrumbs = this.breadcrumbs.slice(0, index + 1);
      this.store.dispatch(
        UpdateProjectDocumentsBreadcrumbs({
          breadcrumbs: updatedBreadcrumbs,
          currentRootId: targetBreadcrumb.id,
        })
      );
    }
    this.expandedRows.clear();
    this.processData();
  }

  isRowExpanded(row: any): boolean {
    return this.expandedRows.has(row.id);
  }

  getIndentStyle(level: number): { [key: string]: string } {
    return {
      'padding-left': `${level * 24}px`,
    };
  }

  onThumbnailError(event: Event, rowId: string) {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    this.thumbnailErrors.add(rowId);
  }

  hasThumbnailError(rowId: string): boolean {
    return this.thumbnailErrors.has(rowId);
  }

  openDocumentCarousel(event: Event, row: any) {
    event.stopPropagation();

    if (row.type === 'folder') {
      return;
    }

    const rootData = this.data?.data || this.data;
    const flattenedRows = rootData?.flattenedRows || [];
    const rowMap = new Map<string, any>(flattenedRows.map((r: any) => [r.id, r]));

    let attachments: any[] = [];
    let currentAttachment: any = null;

    if (row.type === 'document') {
      const documentData = row.originalData || rowMap.get(row.id) || row;
      attachments = documentData.attachments || [];

      if (attachments.length === 0) {
        attachments = [
          {
            id: documentData.id,
            original_name: documentData.name,
            original_url: documentData.original_url || documentData.preview_url || documentData.thumb_url,
            preview_url: documentData.preview_url || documentData.thumb_url,
            thumb_url: documentData.thumb_url,
            fileType: documentData.fileType || 'document',
            mime: documentData.mime,
          },
        ];
        currentAttachment = attachments[0];
      } else {
        currentAttachment = attachments[0];
      }
    } else if (row.type === 'attachment') {
      const attachmentData = row.originalData || rowMap.get(row.id) || row;

      const parentDocument = rowMap.get(row.parentId);

      if (parentDocument && parentDocument.attachments && parentDocument.attachments.length > 0) {
        attachments = parentDocument.attachments;
        currentAttachment = attachments.find((att: any) => att.id === attachmentData.id) || attachments[0];
      } else {
        attachments = [attachmentData];
        currentAttachment = attachmentData;
      }
    }

    if (attachments.length > 0) {
      const formattedAttachments = attachments.map((att: any) => ({
        id: att.id,
        original_name: att.name || att.original_name,
        original_url: att.original_url || att.preview_url || att.thumb_url,
        preview_url: att.preview_url || att.thumb_url,
        preview: att.preview_url || att.thumb_url,
        thumb_url: att.thumb_url,
        fileType:
          att.fileType ||
          (att.mime?.startsWith('image/') ? 'picture' : att.mime?.startsWith('video/') ? 'video' : 'document'),
        mime: att.mime,
      }));

      const currentFormattedAttachment = currentAttachment
        ? formattedAttachments.find(a => a.id === currentAttachment.id)
        : null;
      this.appService.openCarousel(event, formattedAttachments as any, currentFormattedAttachment as any);
    }
  }

  delete(row: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete',
        `Are you sure you want to delete this ${row.type === 'folder' ? 'folder' : 'file'}?`
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (row.type === 'folder') {
          const payload = {
            folderId: row.id,
            projectId: this.projectId,
          };
          this.store.dispatch(DeleteFolderRequest({ payload }));
        } else {
          const payload = {
            documentId: row.id,
            projectId: this.projectId,
          };
          this.store.dispatch(DeleteDocumentRequest({ payload }));
        }

        this.analyticsService.capture('document_deleted', {
          project_id: this.projectId,
        });
      }
    });
  }

  rename(row) {
    this.dialog.open(EditNameComponent, {
      disableClose: false,
      width: '460px',
      height: '445px',
      data: {
        projectId: this.projectId,
        data: row,
      },
    });
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    const imageFormData = new FormData();

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      imageFormData.append('inputFiles', file);
    }

    if (imageFormData.has('inputFiles')) {
      let path = '';

      if (this.breadcrumbs.length > 0) {
        const breadcrumbPath = this.breadcrumbs.map(b => b.name).join('/');
        path = `/${breadcrumbPath}`;
      } else {
        path = null;
      }

      const payload = {
        imageFormData,
        projectId: this.projectId,
        ...(this.data?.id ? { folder_id: this.data?.id } : {}),
        ...(path ? { folder_path: path } : {}),
      };

      this.store.dispatch(AddDocumentRequest({ payload }));

      this.analyticsService.capture('document_uploaded', {
        project_id: this.projectId,
        file_count: files.length,
      });
    }

    this.fileInputRef.nativeElement.value = '';
  }

  isPreviewable(row: any): boolean {
    if (!row || row.type === 'folder') {
      return false;
    }
    const originalData = row.originalData || row;
    const mime = originalData.attachments?.[0]?.mime || originalData.mime || row.mime || '';
    const fileType = row.fileType || '';
    return mime.startsWith('image/') || mime === 'application/pdf' || fileType === 'picture' || fileType === 'video';
  }

  download(row: any) {
    const originalData = row.originalData || row;
    const attachment = originalData.attachments?.[0];
    const url = attachment?.original_url || attachment?.preview_url || originalData.original_url || originalData.preview_url || row.thumb_url;
    if (url) {
      const fileName = row.name || 'download';
      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.click();
          window.URL.revokeObjectURL(blobUrl);
        });
    }
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
